// Human-readable changelog surfaced on /changelog.
//
// Each entry is ONE production release, not one day: keyed on the
// production build SHA (7-char) with the release date shown next
// to it. Multiple releases can happen on the same day; each gets
// its own block so a supervisor can see "build XYZ shipped on
// Aug 22 with these five changes."
//
// The "pending" sentinel represents work that's on preview but
// hasn't been promoted to production yet. Only rendered on the
// preview channel — the production bundle only knows about
// releases that had already shipped when it was built.
//
// Write each bullet as if you're describing the update to a
// supervisor scanning the app on their phone — no jargon, no
// filenames, no SHAs. Group related commits into ONE bullet
// where possible. The raw git log is still visible on the same
// page behind a "Technical details" toggle (platform developer
// only), so nothing gets lost.
//
// PROMOTE WORKFLOW: when the user promotes the "pending" block to
// production, replace its `sha: "pending"` with the actual 7-char
// SHA of the merge commit that landed on main, add `date`, and
// start a fresh empty pending block above for the next round.

export type ChangeType = "new" | "improved" | "fixed";

export type ChangelogChange = {
  type: ChangeType;
  title: string;
  detail?: string;
};

export type ChangelogRelease = {
  // 7-char SHA of the production build this release shipped as,
  // OR the literal string "pending" for work that's on preview
  // but not yet promoted to production.
  sha: string;
  // YYYY-MM-DD the build was promoted to production. Omit for
  // pending releases.
  date?: string;
  changes: ChangelogChange[];
};

export const CHANGELOG: ChangelogRelease[] = [
  {
    sha: "pending",
    changes: [
      {
        type: "improved",
        title: "Developer channel banner now shows on both preview and production",
        detail:
          "The floating chip in the top-right (visible only to the "
          + "platform developer account) is now symmetric: on "
          + "production it says 'Live production build' and offers "
          + "a Switch to preview button; on preview it stays "
          + "amber with the Promote to production button. Makes "
          + "hopping between channels during testing a single "
          + "click either direction.",
      },
    ],
  },
  {
    sha: "cbacc9e",
    date: "2026-08-22",
    changes: [
      {
        type: "improved",
        title: "Changelog now groups by production build, not by day",
        detail:
          "Every entry is one release: build number + the date it "
          + "was promoted to production. Multiple releases on the "
          + "same day each get their own block. Work that's on "
          + "preview but not yet promoted shows under a Next "
          + "release header so you can see what's queued before "
          + "clicking Promote.",
      },
      {
        type: "improved",
        title: "Validation errors stay in the picker instead of the top banner",
        detail:
          "A bad code or hour range now shows a red banner inside "
          + "the code picker or the inline editor and keeps the "
          + "form open so you can fix it in place. No more chasing "
          + "the top-of-page error while the picker slams shut.",
      },
    ],
  },
  {
    sha: "2c33c7c",
    date: "2026-08-22",
    changes: [
      {
        type: "improved",
        title: "Cell entries stay clean: 24-hour format enforced",
        detail:
          "Typing hours into a cell now forces 24-hour format. "
          + "\"9-14\" auto-pads to \"09-14\", \"22-06\" is accepted "
          + "for wrap-midnight ranges, and bad entries like \"5\" "
          + "(a bare number), \"9-5\" written for 9AM-5PM, or "
          + "\"2p\" are rejected so garbage never sticks. Letter-"
          + "first entries (SA, XXX, V, TR, OT½, etc.) still work "
          + "exactly the same — code first, hours second, no "
          + "confusion.",
      },
      {
        type: "new",
        title: "Per-day coverage counting for shifts like Animal Control",
        detail:
          "New toggle on the Shift edit form: 'Count coverage per day, "
          + "not per hour.' Turn it on for shifts where a single "
          + "officer working their own hours fully covers the day — "
          + "two ACOs on 8-4 / 9-5 with staggered days off, for "
          + "example. The shift now reads as covered whenever at "
          + "least min-staffing officers have a working assignment "
          + "that date, no matter which hours they cover. Off by "
          + "default so every other shift keeps the normal hourly-"
          + "bucket math.",
      },
      {
        type: "new",
        title: "Jump from an audit entry back to the schedule cell",
        detail:
          "Each assignment row in the Audit log now has a → cell "
          + "chip. Click it to hop straight to the Schedule with "
          + "the affected cell selected, the pay period scrolled to "
          + "that date, and a brief amber pulse so your eye lands "
          + "on the cell. Pairs with 'View change history for this "
          + "cell' so you can move both directions between the "
          + "Schedule and the log.",
      },
      {
        type: "new",
        title: "See the change history for any schedule cell",
        detail:
          "Double-click a cell to open the code picker, then click "
          + "'View change history for this cell' at the bottom. "
          + "Opens the Audit log filtered to every create, update, "
          + "and delete that ever touched that specific "
          + "(officer, date, shift) — going back to when the audit "
          + "log first started, not just the last 500 entries. "
          + "Only visible to users with audit access.",
      },
      {
        type: "new",
        title: "Fix Coverage auto-fills to the actual uncovered hours",
        detail:
          "When you open Fix Coverage on a short shift — including "
          + "when an officer has partial hours in their cell like "
          + "\"6-12\" on a 6-14 shift — the modal now shows the "
          + "uncovered window (e.g. Uncovered: 12:00–14:00) and "
          + "pre-fills the OT hours picker to that gap. Pick the "
          + "covering officer and click Assign OT; no more hand-"
          + "typing the leftover hours.",
      },
      {
        type: "new",
        title: "Preview builds for testing before going live",
        detail:
          "Updates now land at pdshiftmanager.com/preview/ first. "
          + "The platform developer sees a preview banner with a "
          + "Promote to production button — nothing hits the live "
          + "site until it's pushed there.",
      },
      {
        type: "new",
        title: "Click the build number to see what's changed",
        detail:
          "The small build code in the sidebar footer now opens "
          + "this changelog. Every release lists what's new, what "
          + "was improved, and what was fixed.",
      },
      {
        type: "fixed",
        title: "Overtime chip appearing on the wrong day for split coverage",
        detail:
          "When a shift is covered by two officers — one from the "
          + "shift before, one from the shift after — the chip now "
          + "lands on the same calendar day each officer's own home "
          + "shift is scheduled for. Previously the wrap-midnight "
          + "cases (Night covering the first half of Day, Day "
          + "covering the second half of Night) landed on the wrong "
          + "row.",
      },
      {
        type: "fixed",
        title: "Overtime chip wouldn't clear when deleted from the officer's row",
        detail:
          "Clearing an overtime chip in an officer's home row "
          + "(Backspace or Remove code) now actually deletes the "
          + "underlying overtime assignment — even when that "
          + "assignment lives on a different shift.",
      },
      {
        type: "improved",
        title: "Audit log inquiries beyond the last 500 rows",
        detail:
          "Added From / To date filters and a Load older button so "
          + "you can pull audit records from any point in the past. "
          + "Nothing has ever been aged out of the log — this just "
          + "makes older activity reachable from the UI.",
      },
    ],
  },
];
